<!-- src/components/TableTimeSelector.vue -->
<template>
  <el-header v-if="!hideTopChooseTime" height="auto" class="table-time-selector">
    <div class="left-panel"></div>
    <div class="right-panel">
      <div class="right-panel-search">
        <span class="time-label">当前时间：{{ currentTimeLabel }}</span>

        <!-- 时间选择器 -->
        <el-radio-group v-model="innerChooseTime" @change="handleTimeChange">
          <el-radio-button v-for="(option, key) in timeOptions" :key="key" :label="option.name" :value="Number(key)" />
        </el-radio-group>

        <!-- 隐藏的日期选择器 -->
        <div class="hidden-date-picker-container">
          <el-date-picker ref="datePicker" v-model="innerCustomRange" type="daterange" range-separator="至"
            start-placeholder="开始日期" end-placeholder="结束日期" value-format="YYYY-MM-DD" :disabled-date="disabledDate"
            @change="handleCustomRangeChange" @visible-change="datePickerVisibleChange"
            popper-class="custom-date-range-popper" />
        </div>
      </div>
    </div>
  </el-header>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isoWeek from 'dayjs/plugin/isoWeek'

// 扩展 Day.js 功能
dayjs.extend(isSameOrBefore)
dayjs.extend(isoWeek)

const props = defineProps({
  chooseTime: {
    type: Number,
    default: 2 // 默认今天
  },
  customRange: {
    type: Array,
    default: () => []
  },
  hideTopChooseTime: {
    type: Boolean,
    default: false
  }
})

// 添加统一的 change 事件
const emit = defineEmits([
  'update:chooseTime',
  'update:customRange',
  'time-change',
  'custom-range-change',
  'change' // 新增的统一事件
])

// 时间范围选项配置
const timeOptions = {
  1: { name: '昨天', type: 'day', offset: -1, dataGranularity: 1 },
  2: { name: '今天', type: 'day', offset: 0, dataGranularity: 1 },
  3: { name: '本周', type: 'week', offset: 0, dataGranularity: 2 },
  4: { name: '上周', type: 'week', offset: -1, dataGranularity: 2 },
  5: { name: '本月', type: 'month', offset: 0, dataGranularity: 3 },
  6: { name: '上月', type: 'month', offset: -1, dataGranularity: 3 },
  7: { name: '本年', type: 'year', offset: 0, dataGranularity: 4 },
  8: { name: '上年', type: 'year', offset: -1, dataGranularity: 4 },
  9: { name: '自定义', type: 'custom', dataGranularity: null }
}

// 添加初始化标记
const isInitialized = ref(false)
const innerChooseTime = ref(props.chooseTime)
const innerCustomRange = ref(props.customRange)
const currentTimeLabel = ref('')
const datePicker = ref(null)

// 计算日期范围 - 返回该周期的起始日期
const calculateDateRange = (optionValue) => {
  const option = timeOptions[optionValue]
  if (!option || option.type === 'custom') return { date: '' }

  const now = dayjs()
  let date

  switch (option.type) {
    case 'day':
      date = now.add(option.offset, 'day').format('YYYY-MM-DD')
      break
    case 'week':
      date = now.add(option.offset, 'week').startOf('isoWeek').format('YYYY-MM-DD')
      break
    case 'month':
      date = now.add(option.offset, 'month').startOf('month').format('YYYY-MM-DD')
      break
    case 'year':
      date = now.add(option.offset, 'year').startOf('year').format('YYYY-MM-DD')
      break
    default:
      date = now.format('YYYY-MM-DD')
  }

  return { date }
}

// 获取当前选择的时间数据
const getCurrentTimeData = () => {
  if (innerChooseTime.value === 9) {
    // 自定义日期范围
    if (innerCustomRange.value?.length === 2) {
      return {
        type: 'custom',
        startDate: innerCustomRange.value[0],
        endDate: innerCustomRange.value[1]
      }
    }
    return null
  } else {
    // 预设时间段
    const option = timeOptions[innerChooseTime.value]
    const dateRange = calculateDateRange(innerChooseTime.value)
    return {
      type: 'preset',
      date: dateRange.date,
      dataGranularity: option.dataGranularity
    }
  }
}

// 触发统一事件
const emitTimeData = () => {
  const timeData = getCurrentTimeData()
  if (timeData) {
    emit('change', timeData)
  }
}

// 更新当前时间标签
const updateTimeLabel = () => {
  const timeData = getCurrentTimeData()

  if (!timeData) {
    currentTimeLabel.value = '请选择日期范围'
    return
  }

  if (timeData.type === 'custom') {
    currentTimeLabel.value = `${timeData.startDate} 至 ${timeData.endDate}`
  } else {
    const option = timeOptions[innerChooseTime.value]
    let endDate = ''

    switch (option.type) {
      case 'day':
        currentTimeLabel.value = timeData.date
        return
      case 'week':
        endDate = dayjs(timeData.date).add(6, 'day').format('YYYY-MM-DD')
        break
      case 'month':
        endDate = dayjs(timeData.date).endOf('month').format('YYYY-MM-DD')
        break
      case 'year':
        endDate = dayjs(timeData.date).endOf('year').format('YYYY-MM-DD')
        break
    }

    currentTimeLabel.value = `${timeData.date} 至 ${endDate}`
  }
}

// 当前日期选择下拉出现
const datePickerVisibleChange = (value) => {
  if (value) {
    innerChooseTime.value = 9
    emit('update:chooseTime', 9)
    emitTimeData()
  }
}

// 自定义日期范围处理
const handleCustomRangeChange = (val) => {
  innerCustomRange.value = val
  emit('update:customRange', val)
  updateTimeLabel()
  emit('custom-range-change', val)
  emitTimeData() // 触发统一事件
}

// 时间选择变化处理
const handleTimeChange = () => {
  emit('update:chooseTime', innerChooseTime.value)
  updateTimeLabel()
  emit('time-change', innerChooseTime.value)
  emitTimeData() // 触发统一事件

  if (innerChooseTime.value === 9) {
    openDatePicker()
  }
}

// 打开日期选择面板
const openDatePicker = () => {
  nextTick(() => {
    datePicker.value?.focus()
  })
}

// 禁止选择未来日期
const disabledDate = (time) => time.getTime() > Date.now()

// 监听传入的 chooseTime 变化
watch(() => props.chooseTime, (newVal) => {
  innerChooseTime.value = newVal
  updateTimeLabel()
  // 非初始化的时候触发事件
  if (isInitialized.value) {
    emitTimeData()
  }
}, { immediate: true })

// 监听传入的 customRange 变化
watch(() => props.customRange, (newVal) => {
  innerCustomRange.value = newVal
  updateTimeLabel()
  emitTimeData() // 触发统一事件
}, { immediate: true })

onMounted(() => {
  nextTick(() => {
    isInitialized.value = true
  })
})
</script>

<style lang="scss" scoped>
.table-time-selector {
  height: auto !important;
  padding: 16px 20px;

  .right-panel-search {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    position: relative;

    .time-label {
      font-size: 14px;
      color: #606266;
      min-width: 120px;
      font-weight: 600;
      text-align: right;
      flex-shrink: 0;
    }

    .hidden-date-picker-container {
      position: absolute;
      top: 0;
      right: 0;
      width: 60px;
      height: 100%;
      opacity: 0;
      z-index: 1000;
      pointer-events: auto;

      :deep(.el-date-editor) {
        width: 100%;
        height: 100%;
        cursor: pointer;

        .el-range-input {
          opacity: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        .el-range-separator {
          display: none;
        }
      }
    }
  }
}

@media (max-width: 992px) {
  .right-panel-search {
    flex-direction: column;
    align-items: flex-start !important;

    .time-label {
      text-align: left !important;
      min-width: auto !important;
    }
  }

  .el-radio-group {
    width: 100%;
    justify-content: space-between;
  }
}
</style>